// src/App.jsx - Enhanced with simplified iOS PWA Support
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ChakraProvider } from '@chakra-ui/react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { UAParser } from 'ua-parser-js';
import AccountSetup from './pages/AccountSetup';

// Theme and styles
import theme from './theme';
import './styles/index.css';

// Context providers
import { AuthProvider, useAuthContext } from './context/AuthContext';

// Components and pages
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Performance from './pages/Performance';
import ApiSettings from './pages/ApiSettings';
import TradeLogs from './pages/TradeLogs';
import LoginPage from './pages/LoginPage';

// Enhanced PWA Installer
import AdvancedPWAInstaller from './components/AdvancedPWAInstaller';

// Create a client for React Query
// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Auth hatalarında asla retry yapma
        if (error?.authError || 
            error?.handled ||
            error?.response?.status === 401 ||
            error?.response?.status === 403) {
          console.log('Auth hatası tespit edildi, retry yapılmıyor');
          return false;
        }
        // Diğer hatalar için en fazla 2 kez retry yap
        return failureCount < 2;
      },
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 10, // 10 minutes
      refetchOnWindowFocus: false, // Varsayılan olarak kapalı
      onError: (error) => {
        // Query'ler için global error handler
        console.error('React Query error:', error);
        
        if (error?.authError || 
            error?.response?.status === 401 ||
            error?.response?.status === 403) {
          console.error('React Query auth hatası tespit edildi');
          
          // Auth error event'i tetikle
          window.dispatchEvent(new CustomEvent('auth-error', {
            detail: {
              source: 'reactQuery',
              error: error,
              timestamp: Date.now()
            }
          }));
        }
      }
    },
    mutations: {
      retry: false, // Mutation'lar için retry yapma
      onError: (error) => {
        // Mutation'lar için global error handler
        console.error('React Query mutation error:', error);
        
        if (error?.authError || 
            error?.response?.status === 401 ||
            error?.response?.status === 403) {
          console.error('React Query mutation auth hatası tespit edildi');
          
          // Auth error event'i tetikle
          window.dispatchEvent(new CustomEvent('auth-error', {
            detail: {
              source: 'reactQueryMutation',
              error: error,
              timestamp: Date.now()
            }
          }));
        }
      }
    }
  },
});

// Enhanced PWA Initialization
const initializePWAFeatures = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      console.log('Service Worker registered successfully:', registration);
      
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              window.dispatchEvent(new CustomEvent('pwa-update-available', {
                detail: { registration }
              }));
            }
          });
        }
      });
      
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
    }
  }
};

// Simplified iOS PWA Detection and Setup using UAParser
const setupIOSPWASupport = () => {
  const parser = new UAParser();
  const result = parser.getResult();
  
  // Enhanced iOS detection using UAParser + fallback checks
  const isIOSDevice = 
    result.os.name === 'iOS' ||
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
    (result.device.type === 'mobile' && result.os.name === 'Mac OS') ||
    (result.device.vendor === 'Apple' && result.device.type === 'mobile');
  
  const isInStandaloneMode = window.navigator.standalone === true;
  
  const isSafari = 
    result.browser.name === 'Safari' ||
    result.browser.name === 'Mobile Safari' ||
    (/Safari/.test(navigator.userAgent) && !/Chrome|CriOS|OPiOS|FxiOS|EdgiOS/.test(navigator.userAgent));
  
  console.log('Enhanced iOS Detection:', {
    isIOSDevice,
    isInStandaloneMode,
    isSafari,
    uaParserResult: result,
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    maxTouchPoints: navigator.maxTouchPoints,
    standalone: window.navigator.standalone
  });
  
  if (isIOSDevice && !isInStandaloneMode) {
    console.log('iOS PWA setup triggering for device...');
    
    // Add iOS-specific classes immediately
    document.body.classList.add('ios-device');
    
    // Multiple triggers with different delays to ensure reliability
    const triggerIOSPWA = (attempt = 1) => {
      console.log(`Triggering iOS PWA event - Attempt ${attempt}`);
      
      window.dispatchEvent(new CustomEvent('pwa-install-available', {
        detail: { 
          prompt: null,
          platform: 'ios',
          method: 'manual',
          canInstall: true,
          browser: isSafari ? 'safari' : 'other',
          deviceInfo: result,
          attempt
        }
      }));
    };
    
    // Immediate trigger
    triggerIOSPWA(1);
    
    // Delayed triggers for reliability
    setTimeout(() => triggerIOSPWA(2), 500);
    setTimeout(() => triggerIOSPWA(3), 1500);
    setTimeout(() => triggerIOSPWA(4), 3000);
    
    return true;
  } else if (isIOSDevice && isInStandaloneMode) {
    console.log('iOS PWA mode detected - app already installed');
    document.body.classList.add('ios-device', 'ios-pwa');
  }
  
  return false;
};

// Protected Route Component
const ProtectedRoute = ({ children, requiresSetup = false }) => {
  const { isAuthenticated, isAuthenticating, user } = useAuthContext();
  
  if (isAuthenticating) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(180deg, rgb(0, 0, 0) 0%, rgb(7, 35, 7) 100%)',
        color: 'white'
      }}>
        <div>Loading...</div>
      </div>
    );
  }
  
  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Setup required but user has already completed setup
  if (requiresSetup && user?.role > 0) {
    return <Navigate to="/" replace />;
  }
  
  // Main app routes but user needs to complete setup
  if (!requiresSetup && user?.role === 0) {
    return <Navigate to="/account-setup" replace />;
  }
  
  // Conditions passed, render the children
  return children;
};

// App Router Component
const AppRouter = () => {
  const { isAuthenticated } = useAuthContext();
  
  return (
    <Routes>
      <Route 
        path="/login" 
        element={
          isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />
        } 
      />
      
      <Route path="/account-setup" element={
        <ProtectedRoute requiresSetup={true}>
          <AccountSetup />
        </ProtectedRoute>
      } />
      
      <Route path="/" element={
        <ProtectedRoute>
          <Layout>
            <Dashboard />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/performance" element={
        <ProtectedRoute>
          <Layout>
            <Performance />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/api-settings" element={
        <ProtectedRoute>
          <Layout>
            <ApiSettings />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/trade-logs" element={
        <ProtectedRoute>
          <Layout>
            <TradeLogs />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Main App Component
const App = () => {
  // Initialize PWA features


useEffect(() => {
  // Service Worker mesajlarını dinle
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'AUTH_ERROR') {
        console.log('Service Worker auth hatası bildirdi');
        window.dispatchEvent(new CustomEvent('auth-error', {
          detail: { 
            source: 'serviceWorker', 
            status: event.data.status,
            timestamp: Date.now()
          }
        }));
      }
    });
  }
  
  // Auth error event'lerini dinle (debug için)
  const handleAuthError = (event) => {
    console.log('Global auth error event:', event.detail);
  };
  
  window.addEventListener('auth-error', handleAuthError);
  
  return () => {
    window.removeEventListener('auth-error', handleAuthError);
  };
}, []);


  useEffect(() => {
    let mounted = true;
    
    const initPWA = async () => {
      try {
        console.log('Starting PWA initialization...');
        
        // Initialize service worker
        await initializePWAFeatures();
        
        // Setup iOS PWA support
        const isIOSSetup = setupIOSPWASupport();
        
        if (mounted) {
          console.log('PWA initialization complete', { 
            isIOSSetup,
            timestamp: new Date().toISOString()
          });
        }
      } catch (error) {
        console.error('PWA initialization failed:', error);
      }
    };
    
    // Small delay to ensure DOM is ready
    setTimeout(initPWA, 100);
    
    return () => {
      mounted = false;
    };
  }, []);

  // Simplified PWA install prompt handling
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      console.log('beforeinstallprompt event triggered');
      e.preventDefault();
      e.stopPropagation();
      
      window.deferredPrompt = e;
      
      window.dispatchEvent(new CustomEvent('pwa-install-available', {
        detail: { 
          prompt: e, 
          platform: 'android',
          method: 'prompt',
          canInstall: true
        }
      }));
    };

    const handleAppInstalled = (e) => {
      console.log('PWA installed successfully');
      
      window.deferredPrompt = null;
      
      window.dispatchEvent(new CustomEvent('pwa-installed', {
        detail: { 
          timestamp: Date.now(),
          userAgent: navigator.userAgent
        }
      }));
      
      if (typeof window.gtag !== 'undefined') {
        window.gtag('event', 'pwa_installed', {
          event_category: 'PWA',
          event_label: 'User installed PWA'
        });
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && 'serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then(registration => {
          registration.update();
        }).catch(console.error);
      }
    };

    // Register event listeners
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);


  

  // Performance monitoring for PWA
  useEffect(() => {
    if ('performance' in window) {
      const handleLoad = () => {
        try {
          const perfData = performance.getEntriesByType('navigation')[0];
          
          if (perfData) {
            console.log('PWA Performance Metrics:', {
              loadTime: perfData.loadEventEnd - perfData.loadEventStart,
              domContentLoaded: perfData.domContentLoadedEventEnd - perfData.loadEventStart,
              firstPaint: performance.getEntriesByType('paint')[0]?.startTime || 0
            });
            
            if (typeof window.gtag !== 'undefined') {
              window.gtag('event', 'pwa_performance', {
                event_category: 'PWA',
                custom_map: {
                  load_time: Math.round(perfData.loadEventEnd - perfData.loadEventStart)
                }
              });
            }
          }
        } catch (error) {
          console.error('Performance monitoring error:', error);
        }
      };

      window.addEventListener('load', handleLoad);
      
      return () => {
        window.removeEventListener('load', handleLoad);
      };
    }
  }, []);

  // Network status monitoring for PWA
  useEffect(() => {
    const handleOnline = () => {
      console.log('PWA back online');
      window.dispatchEvent(new CustomEvent('pwa-online', {
        detail: { timestamp: Date.now() }
      }));
    };

    const handleOffline = () => {
      console.log('PWA went offline');
      window.dispatchEvent(new CustomEvent('pwa-offline', {
        detail: { timestamp: Date.now() }
      }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Simplified iOS-specific setup - just add classes and debug
  useEffect(() => {
    const parser = new UAParser();
    const result = parser.getResult();
    
    const isIOSDevice = 
      result.os.name === 'iOS' ||
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    const isIOSPWA = window.navigator.standalone === true;
    
    if (isIOSDevice) {
      console.log('iOS device detected in App component:', {
        isStandalone: isIOSPWA,
        uaParserOS: result.os.name,
        device: result.device,
        browser: result.browser,
        standalone: window.navigator.standalone,
        userAgent: navigator.userAgent
      });
      
      document.body.classList.add('ios-device');
      
      if (isIOSPWA) {
        document.body.classList.add('ios-pwa');
        console.log('iOS PWA mode confirmed');
      } else {
        console.log('iOS browser mode - PWA installer should be available');
      }
    }
    
    return () => {
      document.body.classList.remove('ios-device', 'ios-pwa');
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ChakraProvider theme={theme}>
        <Router>
          <AuthProvider>
            <div className="App">
              <AppRouter />
              
              {/* Enhanced PWA Installer - will show on iOS with improved detection */}
              <AdvancedPWAInstaller />
            </div>
          </AuthProvider>
        </Router>
        
        {process.env.NODE_ENV === 'development' && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
      </ChakraProvider>
    </QueryClientProvider>
  );
};

export default App;